import { http, ApiError } from '@/chassis/data/services/http-client';
import { permissions as permissionCatalog } from '@/access-control.config';
import { userSharedService } from '@/domains/pages/0.1-user-pages/UserShared/service';
import { PORTAL_DISPLAY_NAME_STORAGE_KEY } from '@/domains/pages/0.1-user-pages/UserShared/display-name';
import type { AuthSnapshot, AuthUser } from './AuthProvider';

const ACCESS_TOKEN_KEY = 'auth.token';
const REFRESH_TOKEN_KEY = 'auth.refreshToken';
const SESSION_ID_KEY = 'auth.sessionId';
const GUEST_FLAG_KEY = 'auth.isGuest';

export interface LoginCredentials {
    cpf: string;
    password: string;
    captchaToken?: string;
}

export interface LoginTokens {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
    tokenType?: string;
    sessionId?: string;
}

export interface AuthorizationPayload {
    permissions: string[];
    roles?: string[];
    userId?: string;
    systemAdmin?: boolean;
}

export interface LoginResult {
    snapshot: AuthSnapshot;
    tokens: LoginTokens;
}

export function storeTokens(tokens: LoginTokens) {
    if (tokens.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    }
    if (tokens.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
    if (tokens.sessionId) {
        localStorage.setItem(SESSION_ID_KEY, tokens.sessionId);
    }
}

export function clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(GUEST_FLAG_KEY);
}

export function getStoredTokens(): LoginTokens | null {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || undefined;
    const sessionId = localStorage.getItem(SESSION_ID_KEY) || undefined;
    if (!accessToken) {
        return null;
    }
    return { accessToken, refreshToken, sessionId };
}

function normalizeCpf(cpf: string): string {
    return (cpf || '').replace(/\D/g, '');
}

async function fetchAuthorization(): Promise<AuthorizationPayload> {
    try {
        const data = await http.get<AuthorizationPayload>('/auth/authorization/permissions');
        return {
            permissions: data?.permissions ?? [],
            roles: data?.roles ?? [],
            userId: data?.userId,
            systemAdmin: data?.systemAdmin === true,
        };
    } catch (error) {
        throw error;
    }
}

function derivePermissionsFromRepresentations(representationTypes: string[]): string[] {
    const derived = new Set<string>();
    const typesSet = new Set(representationTypes);

    if (typesSet.has('PREGOEIRO') || typesSet.has('AGENTE_CONTRATACAO')) {
        derived.add('publictender-form:read');
        derived.add('publictender-form:write');
        derived.add('publictender-auctioneer:read');
        derived.add('publictender-auctioneer:write');
        derived.add('publictender-bidding:read');
        derived.add('publictender-bidding:write');
    }

    if (typesSet.has('EQUIPE_APOIO')) {
        derived.add('publictender-auctioneer:read');
        derived.add('publictender-bidding:read');
    }

    if (typesSet.has('AUTORIDADE_SUPERIOR')) {
        derived.add('publictender-auctioneer:read');
        derived.add('publictender-bidding:read');
        derived.add('publictender-approval:read');
        derived.add('publictender-approval:write');
    }

    if (typesSet.has('REPRESENTACAO_LEGAL') || typesSet.has('REPRESENTANTE_LEGAL')) {
        derived.add('publictender-list:read');
        derived.add('publictender-bidding:read');
        derived.add('publictender-bidding:write');
    }

    return Array.from(derived);
}

async function fetchRepresentationTypes(userId?: string | null): Promise<string[]> {
    if (isGuestSession()) {
        return [];
    }

    const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
    if (!normalizedUserId || normalizedUserId === 'unknown-user') {
        return [];
    }

    try {
        const representations = await userSharedService.listRepresentationsForPortal(undefined, {
            force: true,
            active: true,
            representativeUserId: normalizedUserId,
        });

        return Array.from(new Set(
            representations
                .map((representation) => representation.representationType)
                .filter((type): type is string => typeof type === 'string' && type.trim().length > 0)
        ));
    } catch (error) {
        console.warn('[auth] failed to fetch representation types for permission derivation', error);
        return [];
    }
}

function buildSnapshot(user: AuthUser | null, permissions: string[]): AuthSnapshot {
    return {
        user,
        permissions,
    };
}

function extractUserFromLogin(data: any): AuthUser {
    return {
        id: data?.userId ?? data?.id ?? 'unknown-user',
        name: data?.userName ?? data?.name ?? 'Usuário',
        email: data?.userEmail ?? data?.email,
        cpf: data?.userCpf ?? data?.cpf,
        systemAdmin: data?.systemAdmin === true,
        lastSelectedRepresentationId: data?.lastSelectedRepresentationId ?? null,
    };
}

function applyPermissionFallback(raw: string[] | undefined | null, opts?: { systemAdmin?: boolean }): string[] {
    const normalized = Array.isArray(raw) ? raw.filter(Boolean) : [];
    const set = new Set<string>(normalized);

    if (opts?.systemAdmin) {
        Object.values(permissionCatalog).forEach((permission) => {
            set.add(permission);
        });
    }

    if (!set.size) {
        set.add('auth:login');
    }

    return Array.from(set);
}

export async function loginWithCredentials(credentials: LoginCredentials): Promise<LoginResult> {
    const normalizedCpf = normalizeCpf(credentials.cpf);
    if (!normalizedCpf || normalizedCpf.length !== 11) {
        throw new Error('CPF inválido');
    }

    const payload: Record<string, unknown> = {
        cpf: normalizedCpf,
        password: credentials.password,
    };

    if (credentials.captchaToken) {
        payload.captchaToken = credentials.captchaToken;
    }

    let response: any;
    try {
        response = await http.post('/authentication/login', payload);
    } catch (error) {
        if (error instanceof ApiError) {
            const message = typeof error.message === 'string' && error.message.trim().length > 0 ? error.message : 'Falha na autenticação';
            throw new Error(message);
        }
        throw error instanceof Error ? error : new Error('Falha na autenticação');
    }

    const envelope = response ?? {};
    const tokenPayload = envelope?.data ?? envelope;
    if (!tokenPayload || !tokenPayload.accessToken) {
        const backendError =
            (typeof envelope?.error === 'string' && envelope.error) ||
            (typeof envelope?.message === 'string' && envelope.message) ||
            'Não foi possível autenticar. Tente novamente.';
        throw new Error(backendError);
    }

    const tokens: LoginTokens = {
        accessToken: tokenPayload.accessToken,
        refreshToken: tokenPayload.refreshToken,
        expiresIn: tokenPayload.expiresIn,
        tokenType: tokenPayload.tokenType,
        sessionId: tokenPayload.sessionId || tokenPayload.sessionToken,
    };

    storeTokens(tokens);
    localStorage.removeItem(GUEST_FLAG_KEY);

    let permissions: string[] = [];
    let user: AuthUser | null = null;

    try {
        const authorization = await fetchAuthorization();
        const userId = authorization.userId ?? tokenPayload.userId ?? 'unknown-user';
        const representationTypes = await fetchRepresentationTypes(userId);
        const derivedPermissions = derivePermissionsFromRepresentations(representationTypes);
        permissions = applyPermissionFallback([...authorization.permissions, ...derivedPermissions], { systemAdmin: authorization.systemAdmin === true });
        user = {
            id: userId,
            name: tokenPayload.userName ?? 'Usuário',
            email: tokenPayload.userEmail,
            cpf: tokenPayload.userCpf ?? normalizedCpf,
            systemAdmin: authorization.systemAdmin === true,
            // FIX9b 05/08: papéis (PREGOEIRO etc.) vinham na resposta de
            // autorização mas eram descartados — o card do órgão (FIX9)
            // precisa deles pra mostrar a FUNÇÃO do servidor.
            roles: authorization.roles ?? [],
            representationTypes,
            // Bug #92: persiste no snapshot pro RepresentationSelectionProvider
            // usar no momento da hidratacao.
            lastSelectedRepresentationId: tokenPayload.lastSelectedRepresentationId ?? null,
        };
    } catch (error) {
        user = extractUserFromLogin({ ...tokenPayload, userCpf: tokenPayload.userCpf ?? normalizedCpf });
        permissions = applyPermissionFallback(null, { systemAdmin: user?.systemAdmin === true });
    }

    const snapshot = buildSnapshot(user, permissions);
    return { snapshot, tokens };
}

export async function refreshAuthorization(currentSnapshot?: AuthSnapshot | null): Promise<AuthSnapshot | null> {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        return null;
    }

    try {
        const authorization = await fetchAuthorization();
        const baseUser = currentSnapshot?.user ?? {
            id: authorization.userId ?? 'unknown-user',
            name: 'Usuário',
        };
        const resolvedUserId = authorization.userId ?? baseUser.id;
        const representationTypes = await fetchRepresentationTypes(resolvedUserId);
        const derivedPermissions = derivePermissionsFromRepresentations(representationTypes);
        return buildSnapshot(
            {
                ...baseUser,
                id: resolvedUserId,
                systemAdmin: authorization.systemAdmin ?? baseUser.systemAdmin,
                // FIX9b 05/08: idem login — propaga os papéis pro user.
                roles: authorization.roles ?? (baseUser as any).roles ?? [],
                representationTypes,
            },
            applyPermissionFallback([...authorization.permissions, ...derivedPermissions], { systemAdmin: authorization.systemAdmin === true || baseUser.systemAdmin === true })
        );
    } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            clearTokens();
            return null;
        }
        throw error;
    }
}

/**
 * Login como visitante — cria sessão guest com permissões read-only.
 * Requer captchaToken validado pelo reCAPTCHA no frontend.
 */
export async function loginAsGuest(captchaToken: string): Promise<LoginResult> {
    const response = await http.post<any>('/authentication/guest', { captchaToken });
    const data = response?.data ?? response;

    if (!data?.accessToken) {
        throw new Error(response?.message || 'Falha ao criar sessão de visitante');
    }

    const tokens: LoginTokens = {
        accessToken: data.accessToken,
        tokenType: data.tokenType || 'Bearer',
        expiresIn: data.expiresIn,
    };

    storeTokens(tokens);
    localStorage.setItem(GUEST_FLAG_KEY, 'true');
    localStorage.removeItem(PORTAL_DISPLAY_NAME_STORAGE_KEY);

    const guestUser: AuthUser = {
        id: data.guestId || 'guest',
        name: 'Visitante',
        systemAdmin: false,
        isGuest: true,
    };

    // Guest deve ficar restrito ao dashboard informativo e à pesquisa pública.
    const guestPermissions = [
        'publictender-list:read',
        'publictender-search:read',
    ];

    const snapshot = buildSnapshot(guestUser, guestPermissions);
    return { snapshot, tokens };
}

/**
 * Verifica se a sessão atual é de guest.
 */
export function isGuestSession(): boolean {
    return localStorage.getItem(GUEST_FLAG_KEY) === 'true';
}

export async function logoutSession(timeoutMs = 7000): Promise<void> {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

    const storedTokens = getStoredTokens();
    const persistedSessionId = (typeof window !== 'undefined') ? window.localStorage.getItem(SESSION_ID_KEY) || undefined : undefined;
    const sessionId = storedTokens?.sessionId || persistedSessionId;
    const payload = sessionId ? { sessionId } : undefined;

    try {
        await http.post('/authentication/logout', payload, controller ? { signal: controller.signal } : undefined);
    } catch (error) {
        const isAbortError = typeof error === 'object' && error !== null && 'name' in error && (error as { name?: string }).name === 'AbortError';
        if (isAbortError) {
            console.warn('[auth] logout request aborted after timeout');
        } else {
            // logout endpoint may not be available yet; log and continue
            console.warn('[auth] logout endpoint failed:', error);
        }
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        clearTokens();
    }
}
